import {
  createContext,
  useState,
  FunctionComponent,
  useContext,
  useCallback,
  useMemo,
} from 'react'
import { IColor, ThemeColors } from '@/theme/types'
import { conexa } from '@/theme/themes'
import { Theme, theme as heroTheme } from '@conexasaude/hero'
import merge from 'deepmerge'
import { getCache, removeCache, setCache } from '@/utils/cache'
import useQuery from '@/router/useQuery'

export interface IThemeGeneratorContext {
  theme?: Theme
  setThemeColors(colors: ThemeColors | null): void
  embed: { value: boolean; full: boolean }
  withoutInteractionAttendance: { value: boolean }
}

const ThemeGeneratorContext = createContext<IThemeGeneratorContext>(null)

function generateTheme(primary: IColor, secondary: IColor): Theme {
  const theme: Theme = merge(heroTheme, {
    colors: {
      primary,
      secondary,
    },
  } as Partial<Theme>)
  return theme
}

export const ThemeGeneratorProvider: FunctionComponent = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    const colors = getCache<ThemeColors>('colors')
    if (colors) {
      return generateTheme(colors.primary, colors.secondary)
    }
    return generateTheme(conexa.primary, conexa.secondary)
  })
  const query = useQuery()

  const embed = useMemo(() => {
    return {
      value: !!query.get('embed') || !!query.get('embedFull'),
      full: !!query.get('embedFull'),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const withoutInteractionAttendance = useMemo(() => {
    const value = !!query.get('flagDesativaInteracaoPacienteSalaDeEspera')

    return {
      value,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setThemeColors = useCallback(
    (themeColors: ThemeColors | null): void => {
      if (themeColors) {
        const generetadedTheme = generateTheme(
          themeColors.primary,
          themeColors.secondary,
        )
        setTheme(generetadedTheme)
        setCache('colors', themeColors)
      } else {
        setTheme(generateTheme(conexa.primary, conexa.secondary))
        removeCache('colors')
      }
    },
    [],
  )

  return (
    <ThemeGeneratorContext.Provider
      value={{
        theme,
        setThemeColors,
        embed,
        withoutInteractionAttendance,
      }}
    >
      {children}
    </ThemeGeneratorContext.Provider>
  )
}

export const useThemeGeneratorContext = (): IThemeGeneratorContext => {
  const context = useContext(ThemeGeneratorContext)
  if (!context) {
    throw new Error(
      'useAuthContext must be used within a ThemeGeneratorProvider',
    )
  }
  return context
}
